import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

interface SeoSettings {
  id: number;
  page: string;
  title: string;
  description: string;
  keywords: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonical?: string;
  customMeta?: any;
  updatedAt: string;
}

interface SeoFormData {
  page: string;
  title: string;
  description: string;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  canonical: string;
}

export default function SEOManager() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingSettings, setEditingSettings] = useState<SeoSettings | null>(null);
  const [formData, setFormData] = useState<SeoFormData>({
    page: '',
    title: '',
    description: '',
    keywords: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    canonical: ''
  });

  const { data: seoSettings = [], isLoading } = useQuery({
    queryKey: ['/api/admin/seo'],
    retry: false,
  });

  const saveMutation = useMutation({
    mutationFn: async (data: SeoFormData) => {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/seo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save SEO settings');
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "SEO settings saved successfully",
      });
      setIsCreateDialogOpen(false);
      setEditingSettings(null);
      resetForm();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      page: '',
      title: '',
      description: '',
      keywords: '',
      ogTitle: '',
      ogDescription: '',
      ogImage: '',
      canonical: ''
    });
  };

  const handleEdit = (settings: SeoSettings) => {
    setEditingSettings(settings);
    setFormData({
      page: settings.page,
      title: settings.title,
      description: settings.description,
      keywords: settings.keywords,
      ogTitle: settings.ogTitle || '',
      ogDescription: settings.ogDescription || '',
      ogImage: settings.ogImage || '',
      canonical: settings.canonical || ''
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  const pages = ['home', 'about', 'contact', 'blog', 'portfolio', 'careers'];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">SEO Settings</h2>
          <p className="text-slate-600 dark:text-slate-300">Manage meta tags and SEO optimization</p>
        </div>
        <Dialog open={isCreateDialogOpen || !!editingSettings} onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setEditingSettings(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <i className="fas fa-plus mr-2"></i>
              Add SEO Settings
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSettings ? 'Edit' : 'Create'} SEO Settings</DialogTitle>
              <DialogDescription>
                Configure meta tags and SEO settings for a page
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="page">Page</Label>
                  <Select value={formData.page} onValueChange={(value) => setFormData(prev => ({ ...prev, page: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select page" />
                    </SelectTrigger>
                    <SelectContent>
                      {pages.map(page => (
                        <SelectItem key={page} value={page}>{page.charAt(0).toUpperCase() + page.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="canonical">Canonical URL</Label>
                  <Input
                    id="canonical"
                    value={formData.canonical}
                    onChange={(e) => setFormData(prev => ({ ...prev, canonical: e.target.value }))}
                    placeholder="https://example.com/page"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="title">Page Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                    placeholder="Page Title | Company Name"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="description">Meta Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    required
                    rows={3}
                    placeholder="Brief description of the page content..."
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="keywords">Keywords</Label>
                  <Input
                    id="keywords"
                    value={formData.keywords}
                    onChange={(e) => setFormData(prev => ({ ...prev, keywords: e.target.value }))}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="ogTitle">Open Graph Title</Label>
                  <Input
                    id="ogTitle"
                    value={formData.ogTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, ogTitle: e.target.value }))}
                    placeholder="Title for social media sharing"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="ogDescription">Open Graph Description</Label>
                  <Textarea
                    id="ogDescription"
                    value={formData.ogDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, ogDescription: e.target.value }))}
                    rows={3}
                    placeholder="Description for social media sharing..."
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="ogImage">Open Graph Image URL</Label>
                  <Input
                    id="ogImage"
                    value={formData.ogImage}
                    onChange={(e) => setFormData(prev => ({ ...prev, ogImage: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => {
                  setIsCreateDialogOpen(false);
                  setEditingSettings(null);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving...' : (editingSettings ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {Array.isArray(seoSettings) && seoSettings.map((settings: SeoSettings) => (
            <motion.div
              key={settings.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="capitalize">{settings.page} Page</CardTitle>
                      <CardDescription>{settings.title}</CardDescription>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleEdit(settings)}>
                      <i className="fas fa-edit mr-2"></i>
                      Edit
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Description:</span> {settings.description}</p>
                    <p><span className="font-medium">Keywords:</span> {settings.keywords}</p>
                    {settings.canonical && (
                      <p><span className="font-medium">Canonical:</span> {settings.canonical}</p>
                    )}
                    {settings.ogTitle && (
                      <p><span className="font-medium">OG Title:</span> {settings.ogTitle}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
          
          {(!Array.isArray(seoSettings) || seoSettings.length === 0) && (
            <Card>
              <CardContent className="p-8 text-center">
                <i className="fas fa-search text-4xl text-slate-300 dark:text-slate-600 mb-4"></i>
                <p className="text-slate-600 dark:text-slate-300">No SEO settings configured yet.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}